import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppTopBar } from "@/components/AppTopBar";

const ROLES = ["OWNER", "ADMIN_INVENTORY", "CHECKER_OPERATOR"];

export default function UserAccess() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({ email: "", name: "", password: "", role_code: "ADMIN_INVENTORY" });
  const [editModules, setEditModules] = useState(null); // { user, modules:Set, is_owner }
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState("");

  const load = useCallback(() => {
    Promise.all([api.get("/users"), api.get("/users/modules/catalog")])
      .then(([u, c]) => { setUsers(u.data); setCatalog(c.data.modules); })
      .catch((err) => setError(formatApiErrorDetail(err.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault(); setError(""); setNotice("");
    try {
      await api.post("/users", form);
      setNotice(`User ${form.email} dibuat.`);
      setForm({ email: "", name: "", password: "", role_code: "ADMIN_INVENTORY" });
      load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const patchUser = async (u, patch) => {
    setError(""); setNotice("");
    try { await api.patch(`/users/${u.id}`, patch); load(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const openModules = async (u) => {
    setError("");
    try {
      const { data } = await api.get(`/users/${u.id}/modules`);
      setEditModules({ user: u, modules: new Set(data.modules), is_owner: data.is_owner });
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const toggleMod = (id) => setEditModules((s) => {
    const m = new Set(s.modules); m.has(id) ? m.delete(id) : m.add(id); return { ...s, modules: m };
  });
  const saveModules = async () => {
    setError(""); setNotice("");
    try {
      await api.put(`/users/${editModules.user.id}/modules`, { modules: [...editModules.modules] });
      setNotice(`Akses modul ${editModules.user.email} disimpan.`); setEditModules(null); load();
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const savePw = async () => {
    setError(""); setNotice("");
    try { await api.post(`/users/${pwFor.id}/password`, { password: pw }); setNotice(`Password ${pwFor.email} diperbarui.`); setPwFor(null); setPw(""); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  if (user && user.role !== "OWNER") {
    return (<div className="app-shell"><AppTopBar active="users" /><main className="content"><div className="alert-error" data-testid="access-denied">Akses ditolak.</div></main></div>);
  }

  return (
    <div className="app-shell">
      <AppTopBar active="users" />
      <main className="content">
        {error && <div className="alert-error" data-testid="error-banner">{error}</div>}
        {notice && <div className="alert-ok" data-testid="notice-banner">{notice}</div>}

        <section className="panel" data-testid="user-create-panel">
          <h2 className="panel-title">Buat Pengguna Baru</h2>
          <form onSubmit={createUser} style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div><label className="field-label">Email</label><input className="field-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
            <div><label className="field-label">Nama</label><input className="field-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
            <div><label className="field-label">Password</label><input className="field-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
            <div><label className="field-label">Role</label>
              <select className="field-input" value={form.role_code} onChange={(e) => setForm({ ...form, role_code: e.target.value })} data-testid="new-user-role">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <button className="btn-primary inline" type="submit" data-testid="create-user-button">+ Buat User</button>
          </form>
        </section>

        <section className="panel" data-testid="user-list-panel">
          <h2 className="panel-title">Pengguna & Akses ({users.length})</h2>
          <div className="table-scroll">
            <table className="data-table" data-testid="user-table">
              <thead><tr><th>User</th><th>Role</th><th>Status</th><th>Akses Modul</th><th>Aksi</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td><div>{u.name}</div><div className="biz-sub">{u.email}</div></td>
                    <td>
                      <select className="field-input" value={u.role} disabled={u.role === "OWNER"} onChange={(e) => patchUser(u, { role_code: e.target.value })} data-testid={`user-role-${u.id}`}>
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td><span className={`status-pill ${u.status === "ACTIVE" ? "on" : "off"}`} data-testid={`user-status-${u.id}`}>{u.status}</span></td>
                    <td><button className="btn-mini" onClick={() => openModules(u)} data-testid={`user-modules-${u.id}`}>Atur Modul</button></td>
                    <td className="actions-cell">
                      {u.role !== "OWNER" && (
                        u.status === "ACTIVE"
                          ? <button className="btn-mini danger" onClick={() => patchUser(u, { status: "INACTIVE" })} data-testid={`user-deactivate-${u.id}`}>Nonaktifkan</button>
                          : <button className="btn-mini" onClick={() => patchUser(u, { status: "ACTIVE" })} data-testid={`user-activate-${u.id}`}>Aktifkan</button>
                      )}
                      <button className="btn-mini" onClick={() => { setPwFor(u); setPw(""); }} data-testid={`user-setpw-${u.id}`}>Set Password</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {editModules && (
          <section className="panel" data-testid="modules-editor">
            <h2 className="panel-title">Akses Modul — {editModules.user.email}</h2>
            {editModules.is_owner ? (
              <div className="alert-ok">OWNER selalu memiliki akses penuh ke semua modul.</div>
            ) : (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
                  {catalog.map((m) => (
                    <label key={m.id} style={{ display: "flex", gap: 6, alignItems: "center" }} data-testid={`module-toggle-${m.id}`}>
                      <input type="checkbox" checked={editModules.modules.has(m.id)} onChange={() => toggleMod(m.id)} data-testid={`module-checkbox-${m.id}`} />
                      {m.label}
                    </label>
                  ))}
                </div>
                <div className="form-actions" style={{ marginTop: 14 }}>
                  <button className="btn-primary inline" onClick={saveModules} data-testid="save-modules-button">Simpan Akses</button>
                  <button className="btn-ghost inline" onClick={() => setEditModules(null)} data-testid="cancel-modules-button">Batal</button>
                </div>
              </>
            )}
          </section>
        )}

        {pwFor && (
          <section className="panel" data-testid="password-editor">
            <h2 className="panel-title">Set Password — {pwFor.email}</h2>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}><label className="field-label">Password baru (min 6)</label><input className="field-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="new-password-input" /></div>
              <button className="btn-primary inline" onClick={savePw} disabled={pw.length < 6} data-testid="save-password-button">Simpan</button>
              <button className="btn-ghost inline" onClick={() => setPwFor(null)} data-testid="cancel-password-button">Batal</button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
